import { Button } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AutocompleteField from "elements/AutocompleteField";
import { useState } from "react";
import { HiOutlineHomeModern } from "react-icons/hi2";
import { MdLocalHospital } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

const defaultFilters = {
  advanced_wound_care: false,
  online_advice: false,
  locations: [] as any[],
  skills: [] as any[],
  insurance_types: [] as any[],
};

const insuranceOptions = [
  { value: 'Fonasa', label: 'Fonasa' },
  { value: 'Particular', label: 'Particular' },
  { value: 'Reembolsable con Isapre', label: 'Reembolsable con Isapre' },
];

const STEPS = [
  { label: "Tipo" },
  { label: "Comuna" },
  { label: "Buscar" },
];

export default function KinesiologistsFilters(props: any) {
  const { filters: externalFilters, setFilters: externalSetFilters } = props;
  const isControlled = !!externalSetFilters;

  const [internalFilters, setInternalFilters] = useState(defaultFilters);
  const filters = isControlled ? externalFilters : internalFilters;

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const setFilters = (newFilters: any) => {
    if (isControlled) externalSetFilters(newFilters);
    else setInternalFilters(newFilters);
  };

  const isTypeSelected = filters.advanced_wound_care || filters.online_advice;
  const isLocationSelected = filters.locations.length > 0;

  const stepState = (i: number): "active" | "done" | "inactive" => {
    if (i === 0) return isTypeSelected ? "done" : "active";
    if (i === 1) return isLocationSelected ? "done" : "active";
    return isLocationSelected ? "active" : "inactive";
  };

  const setMode = (key: "advanced_wound_care" | "online_advice") => {
    const alreadySelected = filters[key];
    setFilters({
      ...filters,
      advanced_wound_care: key === "advanced_wound_care" ? !alreadySelected : false,
      online_advice: key === "online_advice" ? !alreadySelected : false,
      locations: [],
      skills: [],
      insurance_types: [],
    });
  };

  const missingFieldsMessage = (): string | null => {
    if (filters.locations.length === 0) {
      return 'Debes seleccionar al menos tu comuna antes de buscar.';
    }
    return null;
  };

  const handleSearch = () => {
    const message = missingFieldsMessage();
    if (message) {
      enqueueSnackbar(message, { variant: 'warning' });
      return;
    }
    const params = new URLSearchParams();
    // Si no se eligió un tipo específico, no filtramos por modalidad (domicilio y consulta ambos).
    params.set("advanced_wound_care", String(isTypeSelected ? filters.advanced_wound_care : true));
    params.set("online_advice", String(isTypeSelected ? filters.online_advice : true));
    filters.locations.forEach((loc: any) => params.append("locations", JSON.stringify(loc)));
    filters.skills.forEach((skill: any) => params.append("skills", JSON.stringify(skill)));
    filters.insurance_types.forEach((item: any) => params.append("insurance_types", JSON.stringify(item)));
    navigate({ pathname: "/kinesiologos", search: params.toString() });
  };

  return (
    <div className="home_filters">

      <div className="search_stepper">
        {STEPS.map((step, i) => {
          const state = stepState(i);
          const lineDone = stepState(i) === "done";
          return (
            <div key={i} className="stepper_item">
              <div className="stepper_row">
                <div className={`stepper_circle stepper_${state}`}>
                  {state === "done" ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`stepper_line ${lineDone ? "stepper_line_done" : ""}`} />
                )}
              </div>
              <span className={`stepper_label stepper_label_${state}`}>{step.label}</span>
            </div>
          );
        })}
      </div>

      <div className="input_row">
        <Button
          variant="contained"
          className={`filter_button ${filters.advanced_wound_care ? "filter_is_active" : ""}`}
          onClick={() => setMode("advanced_wound_care")}
          startIcon={<HiOutlineHomeModern />}
        >
          Kinesiología a Domicilio
        </Button>

        <Button
          variant="contained"
          className={`filter_button ${filters.online_advice ? "filter_is_active" : ""}`}
          onClick={() => setMode("online_advice")}
          startIcon={<MdLocalHospital />}
        >
          Kinesiología en Consulta
        </Button>
      </div>

      <div className="input_row">
        <AutocompleteField
          label="Comunas"
          endpoint="/locations/comunas/list"
          renderValues={["label", "region"]}
          multiple={true}
          value={filters.locations}
          onChange={(data: any) => setFilters({ ...filters, locations: data ? data : [] })}
        />
        <AutocompleteField
          label="Especialidades"
          endpoint="/skills/list"
          multiple={true}
          value={filters.skills}
          onChange={(data: any) => setFilters({ ...filters, skills: data ? data : [] })}
        />
      </div>

      <div className="input_row">
        <AutocompleteField
          label="Atención / pago"
          preloadedList={insuranceOptions}
          multiple={true}
          value={filters.insurance_types}
          onChange={(data: any) => setFilters({ ...filters, insurance_types: data ? data : [] })}
        />
      </div>

      <Button
        variant="contained"
        onClick={handleSearch}
        sx={{
          backgroundColor: '#fff',
          color: '#005F6A',
          fontWeight: 700,
          fontSize: '15px',
          padding: '12px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
          '&:hover': { backgroundColor: '#f0f4ff' },
        }}
      >
        Buscar Profesionales
      </Button>

    </div>
  );
}
